import React, { useMemo } from "react";
import { evaluatePassword, getMinLength } from "@/lib/passwordPolicy";

/**
 * Medidor visual de fortaleza de contraseña con motivos.
 *
 * Props:
 *   password: string actual
 *   userData: { name, email, phone, ... } para detectar datos personales
 *   role: rol del usuario (define longitud mínima)
 *   showWhenEmpty: si false (default), oculta el componente sin contraseña
 *
 * Expone `data-testid` para los tests automáticos:
 *   pwd-strength-bar, pwd-strength-label, pwd-strength-reason-{i}
 */
export default function PasswordStrengthMeter({
  password, userData, role, showWhenEmpty = false,
}) {
  const result = useMemo(
    () => evaluatePassword(password, { userData, role }),
    [password, userData, role]
  );

  if (!password && !showWhenEmpty) return null;

  const minLen = getMinLength(role);
  const score = result.score; // 0..4
  const filled = score + 1;   // 1..5 segmentos rellenos cuando hay algo

  const colorFor = (i) => {
    if (i >= filled) return "bg-slate-200";
    if (score === 0) return "bg-red-500";
    if (score === 1) return "bg-orange-500";
    if (score === 2) return "bg-yellow-500";
    if (score === 3) return "bg-lime-500";
    return "bg-emerald-600";
  };
  const labelColor = {
    0: "text-red-600", 1: "text-orange-600", 2: "text-yellow-700",
    3: "text-lime-700", 4: "text-emerald-700",
  }[score];

  return (
    <div className="mt-2 space-y-1.5" data-testid="pwd-strength">
      <div className="flex gap-1" data-testid="pwd-strength-bar">
        {[0, 1, 2, 3, 4].map(i => (
          <div key={i} className={`h-1.5 flex-1 rounded-sm ${colorFor(i)}`} />
        ))}
      </div>
      <div className="flex items-center justify-between text-[11px]">
        <span className={`font-semibold uppercase tracking-wider ${labelColor}`}
              data-testid="pwd-strength-label">
          {result.label}
        </span>
        <span className="text-slate-400">
          {password.length}/{minLen}+ caracteres
        </span>
      </div>
      {result.reasons.length > 0 && (
        <ul className="text-[11px] text-slate-600 space-y-0.5 pl-3">
          {result.reasons.map((r, i) => (
            <li key={i} data-testid={`pwd-strength-reason-${i}`} className="list-disc">
              {r}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
